import { AfterViewInit, Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { DtcLinkState } from '../../../../shared/models/error-message/dtc-link-state';
import { LinkedDtcsUpdateInfo } from '../../../../shared/models/error-message/linked-dtcs-update-info';
import { DtcLinkInfo } from '../../../../shared/models/error-message/dtc-link-info';
import { initFlowbite } from 'flowbite';

@Component({
  selector: 'app-dtc-link-changes-in-other-versions-table',
  imports: [],
  templateUrl: './dtc-link-changes-in-other-versions-table.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dtc-link-changes-in-other-versions-table.scss',
})
export class DtcLinkChangesInOtherVersionsTable implements AfterViewInit {
  @Input() public linkedDtcsUpdateInfo?: LinkedDtcsUpdateInfo[];

  protected readonly dtcLinkState = DtcLinkState;
  private checkedColumns: Record<string, boolean> = {};

  public ngAfterViewInit(): void {
    initFlowbite();
  }

  protected get orderedUpdateInfos(): LinkedDtcsUpdateInfo[] {
    return [...(this.linkedDtcsUpdateInfo ?? [])].sort(
      (left, right) => Number(right.isEditedEntity) - Number(left.isEditedEntity)
    );
  }

  protected get uniqueDtcFriendlyNumbers(): string[] {
    const uniqueKeys = new Set<string>();

    for (const updateInfo of this.linkedDtcsUpdateInfo ?? []) {
      for (const dtcLinkInfo of updateInfo.dtcLinkInfos) {
        const friendlyNumber = this.getDtcFriendlyNumber(dtcLinkInfo.dtcId);
        if (friendlyNumber) {
          uniqueKeys.add(friendlyNumber);
        }
      }
    }
    return [...uniqueKeys];
  }

  protected getStateValue(updateInfo: LinkedDtcsUpdateInfo, dtcFriendlyNumber: string): DtcLinkState {
    const matchingDtcLinkInfo = updateInfo.dtcLinkInfos.find(
      (dtcLinkInfo) => dtcLinkInfo.dtcFriendlyNumber === dtcFriendlyNumber
    );

    return matchingDtcLinkInfo?.linkState ?? DtcLinkState.dtcMissing;
  }

  protected getDtcLinkInfo(updateInfo: LinkedDtcsUpdateInfo, dtcFriendlyNumber: string): DtcLinkInfo | undefined {
    const matchingDtcLinkInfo = updateInfo.dtcLinkInfos.find(
      (dtcLinkInfo) => dtcLinkInfo.dtcFriendlyNumber === dtcFriendlyNumber
    );

    return matchingDtcLinkInfo;
  }

  protected getRowLabel(updateInfo: LinkedDtcsUpdateInfo): string {
    return updateInfo.swPartNumberFriendlyNumber;
  }

  protected isDisabledCell(updateInfo: LinkedDtcsUpdateInfo, dtcFriendlyNumber: string): boolean {
    return updateInfo.isEditedEntity || this.getStateValue(updateInfo, dtcFriendlyNumber) === DtcLinkState.dtcMissing;
  }

  protected toggleSiblingLinkPropagation(updateInfo: LinkedDtcsUpdateInfo, dtcFriendlyNumber: string): void {
    if (!this.linkedDtcsUpdateInfo) {
      return;
    }
    const matchingDtcLinkInfo = updateInfo.dtcLinkInfos.find(
      (dtcLinkInfo) => dtcLinkInfo.dtcFriendlyNumber === dtcFriendlyNumber
    );
    if (!matchingDtcLinkInfo) {
      return;
    }

    matchingDtcLinkInfo.selected = !matchingDtcLinkInfo.selected;
  }

  protected getLabel(dtcLinkInfo?: DtcLinkInfo, isEditedEntity?: boolean): string {
    const state = dtcLinkInfo?.linkState ?? this.dtcLinkState.dtcMissing;
    switch (state) {
      case this.dtcLinkState.asigned:
        return isEditedEntity ? 'create link' : 'linked';
      case this.dtcLinkState.removed:
        return isEditedEntity ? 'remove link' : 'not linked';
      case this.dtcLinkState.linkCanBeAdded:
        return 'create link';
      case this.dtcLinkState.linkCanBeRemoved:
        return 'remove link';
      default:
        return 'n/a';
    }
  }

  protected getTooltipText(dtcLinkInfo?: DtcLinkInfo): string {
    if (!dtcLinkInfo?.dtcFriendlyNumber || !dtcLinkInfo.implementationHint) {
      return 'No DTC details available';
    }

    return dtcLinkInfo.dtcFriendlyNumber + ': ' + dtcLinkInfo.implementationHint;
  }

  protected isColumnChecked(dtcFriendlyNumber: string): boolean {
    return this.checkedColumns[dtcFriendlyNumber] ?? false;
  }

  protected onCheckboxChangeForColumn($event: Event, dtcFriendlyNumber: string): void {
    const isChecked = ($event.target as HTMLInputElement).checked;
    this.updateCheckedColumns(dtcFriendlyNumber, isChecked);
  }

  private updateCheckedColumns(dtcFriendlyNumber: string, isChecked: boolean): void {
    for (const updateInfo of this.orderedUpdateInfos) {
      const dtcInfo = this.getDtcLinkInfo(updateInfo, dtcFriendlyNumber);
      if (dtcInfo) {
        dtcInfo.selected = isChecked;
      }
    }
    this.checkedColumns[dtcFriendlyNumber] = isChecked;
  }

  private getDtcLinkKey(dtcId: string, dtcFriendlyNumber: string): string {
    return dtcId || dtcFriendlyNumber;
  }

  private getDtcFriendlyNumber(dtcId: string): string | undefined {
    const column = this.dtcColumns.find((col) => col.id !== null && col.id === dtcId);
    return column?.friendlyNumber;
  }

  private get dtcColumns(): { id: string; friendlyNumber: string }[] {
    const columns = new Map<string, { id: string; friendlyNumber: string }>();

    for (const updateInfo of this.orderedUpdateInfos) {
      for (const dtcLinkInfo of updateInfo.dtcLinkInfos) {
        const key = this.getDtcLinkKey(dtcLinkInfo.dtcId, dtcLinkInfo.dtcFriendlyNumber);
        if (!columns.has(key)) {
          columns.set(key, {
            id: dtcLinkInfo.dtcId,
            friendlyNumber: dtcLinkInfo.dtcFriendlyNumber,
          });
        }
      }
    }

    return [...columns.values()];
  }
}
